import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { buildQueryString } from "~/utils/url";
// import { PaymentsQueryOptions, PaymentsResponse, Payment } from "../types";
import { PaymentsQueryOptions, PaymentsResponse, Payment } from "../../types";
import { paymentsKeys } from "./keys";

export const paymentsQueryOptions = (opts: PaymentsQueryOptions) => {
  return queryOptions({
    queryKey: paymentsKeys.payments(opts),
    retry: false,
    queryFn: async () => {
        try {
            const response = await apiClient(`/admin/payments/history${buildQueryString(opts)}`);
            const result = await handleResponse<PaymentsResponse>(response);

            if (!result.success) {
                throw new Error(result.error);
            }

            return result.data;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    },
    select(data: PaymentsResponse) {
      return {
        payments: data.payments?.map((payment: Payment) => ({
          id: payment.payment_id,
          description: payment.description,
          amount: payment.amount,
          status: payment.status,
          paymentDate: payment.payment_date,
          userId: payment.user_id,
          userName: payment.user_name,
          typeId: payment.type_id,
          encounterId: payment.encounter_id,
          paymentType: payment.payment_type,
          program: payment.program,
          deliveryStatus: payment.delivery_status
        })),
        hasMore: !!(data.total > (opts?.limit ?? 10)),
        totalCount: data.total,
      };
    },
  });
};


// Add this type for the details endpoint response
export interface HealthProfile {
  bmi: string;
  completed: boolean;
  gender: string;
  glp_intake: string;
  health_conditions: string[];
  height: string;
  last_intake: string;
  medical_history: string;
  medications: any[]; // You can specify a type if you know the structure
  on_medication: string;
  planned_events: string[];
  recent_dose: string;
  target_weight: number;
  timeframe: string;
  updated_at: string;
  weight: number;
}

export interface MedicalPlan {
  program: string;
  session_id: string | null;
  medication: any; // Specify type if known
  consultation: any; // Specify type if known
  success_url: string;
  // Add other fields as needed
}

export interface Patient {
  active: boolean;
  city: string | null;
  date_of_birth: string;
  delivery_address: {
    city: string;
    phone_number: string;
    state: string;
    street: string;
    zip_code: string;
  };
  email: string;
  first_name: string;
  gender: string;
  has_onboarded: boolean;
  has_plan: boolean;
  has_recommended_task: boolean;
  health_profile: HealthProfile;
  health_conditions: {
    name: string;
    status: string;
    medication: string;
    hba1c: string;
  }[];
  allergies: {
    name: string;
    severity: "Severe" | "Moderate" | "Mild";
    causes: string[];
  }[];
  is_new_user: boolean;
  is_verified: boolean;
  last_name: string;
  medical_plan: MedicalPlan;
  passwordis_verified: boolean;
  weight_loss_pharmacy: {
    fulfillment_type: "preferred" | "recommended";
    pharmacy: {
      name: string;
    };
  };
  phone_number: string;
  programs: string[];
  registration_date: string;
  state: string;
  user_id: string;
  user_type: string;
}

export interface PaymentReference {
  client_reference_id: string;
  created_at: number;
  description: string;
  payment_type: string;
  program: string;
  status: string;
  type_id: string;
  user_id: string;
}

export interface Medication {
  cost: number;
  dosage: string;
  duration: string;
  instructions: string;
  medication_id: string;
  name: string;
  notes: string | null;
  quantity: number;
}

export interface TypeData {
  admin_id: string;
  cost: number;
  created_at: number;
  delivery_status: string;
  medications: Medication[];
  patient_id: string;
  payment_status: string;
  prescribed_on: string;
  prescription_id: string;
  status: string;
}

export interface PaymentDetailsResponse {
  status: string;
  message: string | null;
  type: string;
  type_data: TypeData;
  payment_reference: PaymentReference;
  patient: Patient;
}

export const patientPaymentHistoryQueryOptions = (patient_id: string, type_id: string, type: string) => {
    return queryOptions({
        queryKey: ["patientPaymentDetails", patient_id, type_id, type],
        retry: false,
        queryFn: async () => {
            try {
                // Ensure 'type' is always included as a query param
                const response = await apiClient(`/admin/payments/${patient_id}/medication/${type_id}/details?type=${encodeURIComponent(type)}`);
                const result = await handleResponse<PaymentDetailsResponse>(response);
                if (!result.success) {
                    throw new Error(result.error || "Failed to fetch payment details");
                }
                if (!result.data || result.data.status !== "success") {
                    throw new Error(result.data?.message || "Failed to fetch payment details");
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
    });
}